import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiClient } from '@/lib/axios';
import type { User } from '@/types/api';

// Actual shape returned by POST /auth/login
interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

// JWT payload shape (sub, email, role are embedded in every token)
interface JwtPayload {
  sub: string;
  email: string;
  role: string;
  iat: number;
  exp: number;
}

/** Decode a JWT without verifying the signature (client-side use only). */
function decodeJwt(token: string): JwtPayload | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/** Build a minimal User object from JWT claims. */
function userFromJwt(payload: JwtPayload): User {
  return {
    id: payload.sub,
    email: payload.email,
    name: payload.email.split('@')[0],
    role: payload.role as User['role'],
    createdAt: new Date().toISOString(),
  };
}

/**
 * Turn a login failure into copy a user can act on. Never surfaces axios's raw
 * "Request failed with status code 401" text — that's a wire-protocol detail,
 * not something a person trying to sign in should have to interpret.
 */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number; data?: { message?: string } } })
      .response;
    const backendMessage = response?.data?.message;
    if (backendMessage) return backendMessage;

    if (response?.status === 401 || response?.status === 403) {
      return 'Incorrect email or password.';
    }
    if (response?.status && response.status >= 500) {
      return 'Something went wrong on our end. Please try again shortly.';
    }
    return fallback;
  }

  if (err && typeof err === 'object' && 'request' in err) {
    return 'Unable to reach the server. Check your connection and try again.';
  }

  return fallback;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  role: string | null;
  /** Resolved "action:subject" keys for the current role (e.g. "read:Policy") — drives feature gating. */
  permissions: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** Attempts left before the backend's 5-req/min login lockout kicks in, from X-RateLimit-Remaining. */
  loginAttemptsRemaining: number | null;
  /** Seconds to wait when the login endpoint returns 429 — the login page renders its own countdown from this. */
  rateLimitRetryAfterSeconds: number | null;
  login: (email: string, password: string) => Promise<void>;
  /**
   * Adopt a token pair obtained outside the password form — today only the SSO
   * callback, which trades a single-use handoff code for the identical pair
   * POST /auth/login returns.
   */
  completeSsoLogin: (code: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  clearError: () => void;
  /** Super Admin always has every permission, without needing a row in `permissions`. */
  hasPermission: (action: string, subject: string) => boolean;
}

/** Best-effort fetch — a permissions-fetch failure should never block login/refresh. */
async function fetchPermissions(): Promise<string[]> {
  try {
    const { data } = await apiClient.get<{ permissions: string[] }>('/me/permissions');
    return data.permissions;
  } catch {
    return [];
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      role: null,
      permissions: [],
      isAuthenticated: false,
      isLoading: false,
      error: null,
      loginAttemptsRemaining: null,
      rateLimitRetryAfterSeconds: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await apiClient.post<LoginResponse>('/auth/login', {
            email,
            password,
          });
          const payload = decodeJwt(data.access_token);
          const user = payload ? userFromJwt(payload) : null;
          set({
            user,
            token: data.access_token,
            refreshToken: data.refresh_token,
            role: payload?.role ?? null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            loginAttemptsRemaining: null,
            rateLimitRetryAfterSeconds: null,
          });
          const permissions = await fetchPermissions();
          set({ permissions });
        } catch (err: unknown) {
          const response = (
            err as {
              response?: {
                status?: number;
                data?: { message?: string; retryAfterSeconds?: number };
                headers?: Record<string, string>;
              };
            }
          ).response;

          if (response?.status === 429) {
            set({
              isLoading: false,
              isAuthenticated: false,
              error: null,
              loginAttemptsRemaining: 0,
              rateLimitRetryAfterSeconds: response.data?.retryAfterSeconds ?? 60,
            });
            throw err;
          }

          const remainingHeader = response?.headers?.['x-ratelimit-remaining'];
          const remaining = remainingHeader !== undefined ? Number(remainingHeader) : NaN;
          const message = extractErrorMessage(err, 'Login failed. Check your credentials.');
          set({
            isLoading: false,
            error: message,
            isAuthenticated: false,
            loginAttemptsRemaining: Number.isFinite(remaining) ? remaining : null,
            rateLimitRetryAfterSeconds: null,
          });
          throw err;
        }
      },

      completeSsoLogin: async (code) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await apiClient.post<LoginResponse>('/auth/oauth/exchange', { code });
          const payload = decodeJwt(data.access_token);
          set({
            user: payload ? userFromJwt(payload) : null,
            token: data.access_token,
            refreshToken: data.refresh_token,
            role: payload?.role ?? null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            loginAttemptsRemaining: null,
            rateLimitRetryAfterSeconds: null,
          });
          const permissions = await fetchPermissions();
          set({ permissions });
        } catch (err: unknown) {
          set({
            isLoading: false,
            isAuthenticated: false,
            error: extractErrorMessage(err, 'Sign-in could not be completed. Please try again.'),
          });
          throw err;
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          role: null,
          permissions: [],
          isAuthenticated: false,
          error: null,
          loginAttemptsRemaining: null,
          rateLimitRetryAfterSeconds: null,
        });
        setTimeout(() => { window.location.href = '/login'; }, 0);
      },

      refresh: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return;
        try {
          const { data } = await apiClient.post<LoginResponse>('/auth/refresh', {
            refreshToken,
          });
          const payload = decodeJwt(data.access_token);
          const user = payload ? userFromJwt(payload) : get().user;
          set({
            user,
            token: data.access_token,
            refreshToken: data.refresh_token,
            role: payload?.role ?? get().role,
            isAuthenticated: true,
          });
          const permissions = await fetchPermissions();
          set({ permissions });
        } catch {
          get().logout();
        }
      },

      clearError: () => set({ error: null }),

      hasPermission: (action, subject) => {
        const { role, permissions } = get();
        if (role === 'SUPER_ADMIN') return true;
        return permissions.includes(`${action}:${subject}`);
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        role: state.role,
        permissions: state.permissions,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
